var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Form as BsForm } from 'react-bootstrap';
import { useForm, FormProvider } from 'react-hook-form';
import { useDeepEffect } from 'utils';
/**
 * `Form` is a custom React component that serves as a form container.
 * It is designed to work with 'react-bootstrap' and 'react-hook-form' for creating dynamic and controlled forms easily.
 * This component provides form state management, validation, and submission handling.
 *
 * @template T - The type of data for the form. It should extend from FieldValues of 'react-hook-form'.
 * @param {BaseFormProps<T>} props - The properties passed to the Form component. These include:
 * @param {React.ReactNode} children - The form fields and elements to be rendered within the form.
 * @param {(data: T) => void} onSubmit - Function to be called when the form is submitted. It receives the form data as an argument.
 * @param {Partial<T>} values - Initial values to populate the form fields.
 * @param {BsFormProps} formProps - Additional properties for configuring the form, which are passed to the underlying 'react-bootstrap' Form component.
 *
 * Note: This component relies on 'react-bootstrap' and 'react-hook-form' for its functionality.
 */
export const Form = (_a) => {
    var { children, onSubmit, onError, values } = _a, formProps = __rest(_a, ["children", "onSubmit", "onError", "values"]);
    const _b = useForm({
        mode: 'onBlur',
        reValidateMode: 'onSubmit',
        values,
        defaultValues: {}
    }), { handleSubmit, control, getValues, setValue, watch, formState, reset } = _b, rest = __rest(_b, ["handleSubmit", "control", "getValues", "setValue", "watch", "formState", "reset"]);
    useDeepEffect(() => {
        reset(values, { keepDirty: false, keepDefaultValues: true });
    }, [values]);
    return (_jsx(FormProvider, Object.assign({}, Object.assign({ handleSubmit,
        control,
        getValues,
        setValue,
        watch,
        formState,
        reset }, rest), { children: _jsx(BsForm, Object.assign({}, formProps, { onSubmit: handleSubmit(onSubmit, onError) }, { children: children({ control, getValues, setValue, watch, errors: formState.errors }) })) })));
};
